import React, { useState, useEffect } from "react";
import styles from "./MasonryGrid.module.css";
import Image from "../common/Image/Image";

export interface Photo {
  id: string;
  src: string;
  srcSet?: string;
  alt: string;
  title: string;
  category: string;
  width: number;
  height: number;
  aspectRatio?: number | string;
  project?: string;
  projectName?: string;
  filename?: string;
}

interface MasonryGridProps {
  photos: Photo[];
  columns?: number;
  onPhotoClick?: (photo: Photo) => void;
  staggerDelay?: number;
  onDownload?: (photo: Photo) => void;
}

export const MasonryGrid: React.FC<MasonryGridProps> = ({
  photos,
  columns = 4,
  onPhotoClick,
  staggerDelay = 80,
  onDownload,
}) => {
  const [visibleItems, setVisibleItems] = useState<Set<string>>(new Set());

  useEffect(() => {
    const timer = setTimeout(() => {
      photos.forEach((photo, index) => {
        setTimeout(() => {
          setVisibleItems((prev) => new Set(prev).add(photo.id));
        }, index * staggerDelay);
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [photos, staggerDelay]);

  const getColumnPhotos = () => {
    const columns_array: Photo[][] = Array.from({ length: columns }, () => []);
    photos.forEach((photo, index) => {
      columns_array[index % columns].push(photo);
    });
    return columns_array;
  };

  const columnPhotos = getColumnPhotos();

  const handleDownload = (e: React.MouseEvent, photo: Photo) => {
    e.stopPropagation();

    if (onDownload) {
      onDownload(photo);
      return;
    }

    // Default download behavior
    const link = document.createElement('a');
    link.href = photo.src;

    let filename = photo.filename || '';
    if (!filename) {
      const urlParts = photo.src.split('/');
      filename = urlParts[urlParts.length - 1] || `${photo.title.toLowerCase().replace(/\s+/g, '-')}.webp`;
    }

    if (!filename.includes('.')) {
      const ext = photo.src.includes('.png') ? 'png' :
                  photo.src.includes('.jpg') || photo.src.includes('.jpeg') ? 'jpg' : 'webp';
      filename = `${filename}.${ext}`;
    }

    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div
      className={styles.masonryGrid}
      style={
        {
          "--columns": columns,
        } as React.CSSProperties
      }
    >
      {columnPhotos.map((column, colIndex) => (
        <div key={colIndex} className={styles.masonryColumn}>
          {column.map((photo) => {
            const isVisible = visibleItems.has(photo.id);

            return (
              <div
                key={photo.id}
                className={`
                  ${styles.photoItem}
                  ${isVisible ? styles.visible : ""}
                `}
                onClick={() => onPhotoClick?.(photo)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onPhotoClick?.(photo);
                  }
                }}
                aria-label={`View ${photo.title}`}
                data-photo-id={photo.id}
              >
                <div className={styles.imageWrapper}>
                  <Image
                    src={photo.src}
                    alt={photo.alt}
                    title={photo.title}
                    width={photo.width}
                    height={photo.height}
                    className={styles.photoImage}
                    loading="lazy"
                    decoding="async"
                    priority={false}
                    placeholder="empty"
                    project={photo.project || "root"}
                    style={{
                      aspectRatio: photo.aspectRatio || "4/3",
                    }}
                  />
                  <div className={styles.photoOverlay}>
                    <div className={styles.photoInfo}>
                      <span className={styles.photoCategory}>
                        {photo.projectName || photo.category}
                      </span>
                    </div>
                    <button
                      className={styles.downloadButton}
                      onClick={(e) => handleDownload(e, photo)}
                      aria-label={`Download ${photo.title}`}
                      title="Download image"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};
